import { defineStore } from 'pinia';
// Relative, not '@/utils/...': vitest has no '@' alias, and this module is
// imported directly by tests/user-sync-*.test.ts.
import { pendingSyncKey, readPendingSync, writePendingSync, clearPendingSync, hasPendingFor, retryDelayMs } from '../utils/pendingSync';

// Update this to clear the store key year over year
const CURRENT_YEAR = 2025;

const KEY_LIST_LOOKUP = {
    sites: 'sites',
    tours: 'tours',
    events: 'events',
    itineraries: 'itineraries',    
    ohcFavoriteSites: 'sites',
    ohcFavoriteEvents: 'events',
    ohcFavoriteTours: 'tours',
    ohcItineraries: 'itineraries',
    ohcEvents: 'events',
};

// Sync-on-reconnect
//
// Offline, addSite/removeSite/etc. still write to localStorage immediately
// and debounce a syncWithServer() call via triggerSync() - but the network
// request just fails, and nothing ever retried it. Local data was safe
// (mergeArrays/mergeItineraries reconcile with the server on the next
// successful sync) but stayed stranded on-device until the user happened to
// make another edit that re-triggered the debounce.
//
// onReconnect is kept a small, framework-free function - just calls
// store.triggerSync() - so it's unit-testable without a window/DOM.
// triggerSync() -> syncWithServer() already gates on syncEnabled and
// isLoggedIn, so this helper deliberately does NOT duplicate those checks:
// firing an unnecessary sync attempt when there's nothing to sync is
// syncWithServer's job to decline, not this helper's job to predict.
//
// Gated on hasPendingSync() when the store can answer it: pushing local data
// that has NOT changed would overwrite edits made on another device since.
// A store without that method (older callers, tests) keeps the old
// always-sync behaviour.
export function onReconnect(store: { triggerSync: () => any; hasPendingSync?: () => boolean }) {
    if (store.hasPendingSync && !store.hasPendingSync()) return;
    store.triggerSync();
}

// Registers onReconnect against a target's "online" event, guarding against
// double-registration. Keyed by target (normally `window`) via a WeakSet
// rather than a single module-level boolean, so: (a) tests can register
// fresh fake targets without needing to reset module state between cases,
// and (b) initUserData running more than once for the same real `window`
// (it can - see call site) is still a safe no-op on the second call.
const reconnectListenerTargets = new WeakSet<object>();

export function registerReconnectSync(store: { triggerSync: () => any; hasPendingSync?: () => boolean }, target?: typeof window, doc?: Pick<Document, 'addEventListener' | 'visibilityState'>) {
    if (import.meta.server) return;
    const win = target ?? (typeof window !== 'undefined' ? window : undefined);
    if (!win || reconnectListenerTargets.has(win)) return;
    reconnectListenerTargets.add(win);
    win.addEventListener('online', () => onReconnect(store));
    // Coming back to the app is the other moment a stranded edit can get
    // through: the phone left the dead zone while the tab was hidden, and
    // 'online' never fired because the browser never thought it was offline.
    const d = doc ?? (target ? undefined : typeof document !== 'undefined' ? document : undefined);
    d?.addEventListener('visibilitychange', () => {
        if (d.visibilityState === 'visible') onReconnect(store);
    });
}

// Helper Functions
function getSaveOrSet(key: string, value: any) {
    const data = localStorage.getItem(key);
    if (data) {
        return JSON.parse(data);
    } else {
        localStorage.setItem(key, JSON.stringify(value));
        return value;
    }
}

export const useUserStore = defineStore('user', {
    state: () => ({
        initialized: false,
        hasVisitedBefore: false,
        sites: [],
        tours: [],
        events: [],
        itineraries: [],
        ghostItinerary: [],
        ghostBaseItinerary: [],
        readNotifications: 0,
        readNotificationIDs: [],
        // Sync status
        syncEnabled: true,
        lastSyncedAt: null as Date | null,
        syncInProgress: false,
        syncError: null as string | null,
        _syncTimeout: null as any,
        // Bumped by every local edit, so a sync that finishes can tell whether
        // more edits landed while its request was in flight.
        _editGeneration: 0,
        _retryTimeout: null as any,
        _retryAttempt: 0,
        hasLocationPermission: false,
        // Track if this device has logged in before
        hasLoggedInBefore: false,
    }),

    actions: {
        initUserData() {
            // Only run on client side
            if (import.meta.server) return;
            // Retry the debounced server sync when connectivity returns.
            // Guarded internally against double-registration, so it's safe
            // that initUserData can run more than once.
            registerReconnectSync(this);
            // Handle user hasVisitedBefore
            this.hasVisitedBefore = getSaveOrSet('hasVisitedBefore' + CURRENT_YEAR, false);
            // Check if device has logged in before
            this.hasLoggedInBefore = getSaveOrSet('hasLoggedInBefore' + CURRENT_YEAR, false);
            // Handle user sites
            this.sites = getSaveOrSet('sites' + CURRENT_YEAR, []);
            // Handle user tours
            this.tours = getSaveOrSet('tours' + CURRENT_YEAR, []);
            // Handle user events
            this.events = getSaveOrSet('events' + CURRENT_YEAR, []);
            // Handle user Itineraries
            this.itineraries = getSaveOrSet('itineraries' + CURRENT_YEAR, []);
            // Handle user readNotifications - Different since we have legacy data
            const readNotificationIDs = localStorage.getItem('readNotificationIDs');
            this.readNotificationIDs = JSON.parse(readNotificationIDs);
            // Load data from server if logged in
            this.loadFromServer().finally(() => {
                // Delay the initialized state to allow for the store to be populated
                setTimeout(() => {
                    this.initialized = true;
                }, 16);
            });
        },

        setHasLocationPermission(value: boolean) {
            this.hasLocationPermission = value;
        },

        async refreshData() {
            if (import.meta.server) return;
            this.itineraries = getSaveOrSet('itineraries' + CURRENT_YEAR, []);
            const ohcItineraries = localStorage.getItem('ohcItineraries');
            return;
        },

        markNotificationsAsRead(notifications: number) {
            this.readNotifications = notifications;
            localStorage.setItem('readNotifications', this.readNotifications.toString());
        },

        updateHasVisitedBefore(vale: boolean) {
            this.hasVisitedBefore = vale;
            localStorage.setItem('hasVisitedBefore', this.hasVisitedBefore.toString());
        },

        // Offline read-only mode. The gate lives in the store as well as on the
        // controls because this is the guarantee: one choke point that a control
        // added later, or a path nobody remembered, cannot get around. The
        // disabled controls are the affordance — they explain it to the user —
        // but they are only ever as complete as the last UI audit.
        //
        // Gated set = exactly the actions that call triggerSync(), because those
        // are the ones whose result reaches the server and can therefore diverge.
        // Device-local state (notifications read, has-visited, ghost itinerary)
        // is deliberately left writable: it never syncs, so it cannot diverge,
        // and blocking it would break the UI offline for no benefit.
        refuseOffline(action: string): boolean {
            if (!isOffline()) return false;
            console.info(`[offline] ${action} refused — the app is read-only while offline`);
            return true;
        },

        removeSite(id: string, list: string = 'sites') {
            // Offline read-only: refuse anything that reaches the server.
            // See docs/superpowers/specs/2026-09-11-offline-read-only-design.md.
            if (this.refuseOffline('removeSite')) return;
            console.log(`🍤 ~ removeSite ~ list:`, list);
            // @ts-ignore
            const realList = KEY_LIST_LOOKUP[list] || list;

            switch (realList) {
                case 'sites':
                    this.sites = this.sites.filter((site) => site !== id);
                    localStorage.setItem('sites' + CURRENT_YEAR, JSON.stringify(this.sites));
                    break;
                case 'tours':
                    this.tours = this.tours.filter((tour) => tour !== id);
                    localStorage.setItem('tours' + CURRENT_YEAR, JSON.stringify(this.tours));
                    break;
                case 'events':
                    this.events = this.events.filter((event) => event !== id);
                    localStorage.setItem('events' + CURRENT_YEAR, JSON.stringify(this.events));
                    break;
                default:
                    console.error('List not found', realList);
                    break;
            }
            // Trigger sync with server
            this.markDirty();
            this.triggerSync();
        },

        addSite(id: string, list: string = 'sites') {
            // Offline read-only: refuse anything that reaches the server.
            // See docs/superpowers/specs/2026-09-11-offline-read-only-design.md.
            if (this.refuseOffline('addSite')) return;
            console.log(`🍤 ~ addSite ~ list:`, list);
            // @ts-ignore
            const realList = KEY_LIST_LOOKUP[list] || list;
            switch (realList) {
                case 'sites':
                    this.sites.push(id);
                    localStorage.setItem('sites' + CURRENT_YEAR, JSON.stringify(this.sites));
                    // Analytics: only the 'sites' list represents a favorite-able site (no unfavorite event exists in the taxonomy).
                    // useAnalytics() has no Vue-component-context dependency (see app/composables/useAnalytics.ts), so it is safe
                    // to call directly from this Pinia option-store action.
                    useAnalytics().track('site_favorited', { site_id: id, site_name: siteNameFrom(useSitesStore().sitesMap, id) });
                    break;
                case 'tours':
                    this.tours.push(id);
                    localStorage.setItem('tours' + CURRENT_YEAR, JSON.stringify(this.tours));
                    break;
                case 'events':
                    this.events.push(id);
                    localStorage.setItem('events' + CURRENT_YEAR, JSON.stringify(this.events));
                    break;
                default:
                    console.error('List not found', realList);
                    break;
            }
            // Trigger sync with server
            this.markDirty();
            this.triggerSync();
        },

        addSiteToItinerary(itineraryName: string, id: string) {
            // Offline read-only: refuse anything that reaches the server.
            // See docs/superpowers/specs/2026-09-11-offline-read-only-design.md.
            if (this.refuseOffline('addSiteToItinerary')) return;
            console.log(`🍤 ~ addSiteToItinerary ~ itineraryName:`, itineraryName);
            const itinerary = this.itineraries.find((itinerary) => itinerary.name === itineraryName);
            if (itinerary) {
                // @ts-ignore
                const found = itinerary.sites.find((site) => site === id);
                if (!found) {
                    // @ts-ignore
                    itinerary.sites.push(id);
                    localStorage.setItem('itineraries' + CURRENT_YEAR, JSON.stringify(this.itineraries));
                    // @ts-ignore
                    useAnalytics().track('itinerary_updated', { action: 'add', size: itinerary.sites.length });
                } else {
                    // console.log('Site already exists in this itinerary');
                    return { error: 'Site already exists in this itinerary' };
                }
            } else {
                const newItinerary = { name: itineraryName, sites: [id] };
                // State too, not just localStorage: syncWithServer() posts
                // this.itineraries, so writing only to storage meant the new
                // itinerary never reached the server until some later route
                // change happened to reload it via refreshData().
                this.itineraries = [...this.itineraries, newItinerary];
                localStorage.setItem('itineraries' + CURRENT_YEAR, JSON.stringify(this.itineraries));
                useAnalytics().track('itinerary_updated', { action: 'add', size: newItinerary.sites.length });
            }
            // Trigger sync with server
            this.markDirty();
            this.triggerSync();
        },

        removeSiteFromItinerary(itineraryName: string, id: string) {
            // Offline read-only: refuse anything that reaches the server.
            // See docs/superpowers/specs/2026-09-11-offline-read-only-design.md.
            if (this.refuseOffline('removeSiteFromItinerary')) return;
            const itinerary = this.itineraries.find((itinerary) => itinerary.name === itineraryName);
            if (itinerary) {
                // @ts-ignore
                itinerary.sites = itinerary.sites.filter((site) => site !== id);
                localStorage.setItem('itineraries' + CURRENT_YEAR, JSON.stringify(this.itineraries));
                // @ts-ignore
                useAnalytics().track('itinerary_updated', { action: 'remove', size: itinerary.sites.length });
                // Trigger sync with server
                this.markDirty();
                this.triggerSync();
            } else {
                console.log('Itinerary not found');
            }
        },

        resetItineraries(value: any) {
            // Offline read-only: refuse anything that reaches the server.
            // See docs/superpowers/specs/2026-09-11-offline-read-only-design.md.
            if (this.refuseOffline('resetItineraries')) return;
            this.itineraries = value;
            localStorage.setItem('itineraries' + CURRENT_YEAR, JSON.stringify(this.itineraries));
            // Trigger sync with server
            this.markDirty();
            this.triggerSync();
        },

        setGhostItinerary(value: any) {
            this.ghostItinerary = value;
        },

        setGhostBaseItinerary(value: any) {
            this.ghostBaseItinerary = value;
        },

        updateSingleItinerary(originalName: string, name: string, sites: any) {
            // Offline read-only: refuse anything that reaches the server.
            // See docs/superpowers/specs/2026-09-11-offline-read-only-design.md.
            if (this.refuseOffline('updateSingleItinerary')) return;
            const itinerary = this.itineraries.find((itinerary) => itinerary.name === originalName);
            if (itinerary) {
                itinerary.name = name;
                itinerary.sites = sites;
                localStorage.setItem('itineraries' + CURRENT_YEAR, JSON.stringify(this.itineraries));
                // @ts-ignore
                useAnalytics().track('itinerary_updated', { action: 'reorder', size: itinerary.sites.length });
                // Trigger sync with server
                this.markDirty();
                this.triggerSync();
            } else {
                console.log('Itinerary not found');
            }
        },

        resetGhostItinerary() {
            this.ghostItinerary = this.ghostBaseItinerary;
        },

        // Pending-sync bookkeeping. See app/utils/pendingSync.ts for why this
        // is persisted and why it is keyed to the account.
        markDirty() {
            if (import.meta.server) return;
            this._editGeneration++;
            const { user } = useAuth();
            writePendingSync(globalThis.localStorage, pendingSyncKey(CURRENT_YEAR), (user.value as any)?.id);
        },

        hasPendingSync(): boolean {
            if (import.meta.server) return false;
            const { user } = useAuth();
            return hasPendingFor(readPendingSync(globalThis.localStorage, pendingSyncKey(CURRENT_YEAR)), (user.value as any)?.id);
        },

        scheduleSyncRetry() {
            if (import.meta.server) return;
            if (this._retryTimeout) return;
            if (!this.hasPendingSync()) return;
            const delay = retryDelayMs(this._retryAttempt);
            this._retryAttempt++;
            this._retryTimeout = setTimeout(() => {
                this._retryTimeout = null;
                this.syncWithServer();
            }, delay);
        },

        // Server sync methods
        async syncWithServer() {
            if (import.meta.server) return;
            if (!this.syncEnabled) return;
            if (this.syncInProgress) return;

            // Check if user is logged in
            const { isLoggedIn } = useAuth();
            if (!isLoggedIn.value) {
                console.log('User not logged in, skipping sync');
                return;
            }

            this.syncInProgress = true;
            this.syncError = null;
            const generation = this._editGeneration;
            let succeeded = false;

            try {
                // Send current data to server
                const response = await $fetch('/api/user/data', {
                    method: 'POST',
                    body: {
                        sites: this.sites,
                        events: this.events,
                        tours: this.tours,
                        itineraries: this.itineraries,
                    },
                });

                if (response.success) {
                    succeeded = true;
                    this.lastSyncedAt = new Date();
                    console.log('Data synced to server successfully');
                } else {
                    this.syncError = 'Sync failed';
                }
            } catch (error) {
                console.error('Failed to sync with server:', error);
                this.syncError = error?.message || 'Sync failed';
            } finally {
                this.syncInProgress = false;
            }

            if (succeeded) {
                this._retryAttempt = 0;
                if (this._retryTimeout) {
                    clearTimeout(this._retryTimeout);
                    this._retryTimeout = null;
                }
                // Only clear the marker if nothing changed while the request
                // was in flight; otherwise those later edits still need a push
                // (the early `syncInProgress` return above swallowed their
                // trigger).
                if (this._editGeneration === generation) clearPendingSync(globalThis.localStorage, pendingSyncKey(CURRENT_YEAR));
                else this.triggerSync();
            } else {
                this.scheduleSyncRetry();
            }
        },

        async loadFromServer() {
            if (import.meta.server) return;

            // Check if user is logged in
            const { isLoggedIn } = useAuth();

            if (!isLoggedIn.value) {
                console.log('User not logged in, skipping server load');
                return;
            }

            // Edits from an earlier session never reached the server (the push
            // failed and the app was closed). Loading now would REPLACE them with
            // the stale server copy, so push them instead; on success the server
            // matches local and there is nothing to load. On failure local data
            // stays as it is and the retry loop keeps trying.
            if (this.hasPendingSync()) {
                console.log('Unsynced local edits found, pushing before loading');
                await this.syncWithServer();
                return;
            }
            // A marker left by a different account must not be acted on, and
            // the load below replaces this device's lists with the signed-in
            // account's anyway.
            clearPendingSync(globalThis.localStorage, pendingSyncKey(CURRENT_YEAR));

            try {
                const response = await $fetch('/api/user/data', {
                    method: 'GET',
                });

                if (response.success && response.data) {
                    // Only merge local data if this is the first login on this device
                    const isFirstLogin = !this.hasLoggedInBefore;

                    if (isFirstLogin) {
                        console.log('First login on this device, merging local and server data');

                        // Get current local data
                        const localSites = [...this.sites];
                        const localEvents = [...this.events];
                        const localTours = [...this.tours];
                        const localItineraries = [...this.itineraries];

                        // Merge server data with local data
                        this.sites = this.mergeArrays(localSites, response.data.sites || []);
                        this.events = this.mergeArrays(localEvents, response.data.events || []);
                        this.tours = this.mergeArrays(localTours, response.data.tours || []);
                        this.itineraries = this.mergeItineraries(localItineraries, response.data.itineraries || []);

                        // Mark that this device has now logged in
                        this.hasLoggedInBefore = true;
                        localStorage.setItem('hasLoggedInBefore' + CURRENT_YEAR, JSON.stringify(true));

                        // If we merged any local data, sync it back to server
                        const hasLocalData = localSites.length > 0 || localEvents.length > 0 || localTours.length > 0 || localItineraries.length > 0;
                        const dataChanged =
                            this.sites.length !== (response.data.sites || []).length ||
                            this.events.length !== (response.data.events || []).length ||
                            this.tours.length !== (response.data.tours || []).length ||
                            this.itineraries.length !== (response.data.itineraries || []).length;

                        if (hasLocalData && dataChanged) {
                            console.log('Local data detected, syncing merged data back to server');
                            await this.syncWithServer();
                        }
                    } else {
                        console.log('Not first login, replacing local data with server data');

                        // Replace local data with server data (no merge)
                        this.sites = response.data.sites || [];
                        this.events = response.data.events || [];
                        this.tours = response.data.tours || [];
                        this.itineraries = response.data.itineraries || [];
                    }

                    // Update localStorage with the data
                    localStorage.setItem('sites' + CURRENT_YEAR, JSON.stringify(this.sites));
                    localStorage.setItem('events' + CURRENT_YEAR, JSON.stringify(this.events));
                    localStorage.setItem('tours' + CURRENT_YEAR, JSON.stringify(this.tours));
                    localStorage.setItem('itineraries' + CURRENT_YEAR, JSON.stringify(this.itineraries));

                    this.lastSyncedAt = new Date();
                    console.log('Data loaded from server successfully');
                }
            } catch (error) {
                console.error('Failed to load from server:', error);
                // Continue with localStorage data if server fails
            }
        },

        // Helper method to merge arrays of IDs (removing duplicates)
        mergeArrays(localArray: string[], serverArray: string[]): string[] {
            const merged = new Set([...serverArray, ...localArray]);
            return Array.from(merged);
        },

        // Helper method to merge itineraries
        mergeItineraries(localItineraries: any[], serverItineraries: any[]): any[] {
            const merged = [...serverItineraries];
            const serverNames = new Set(serverItineraries.map((it) => it.name));
            // Add local itineraries that don't exist on server
            for (const localIt of localItineraries) {
                if (!serverNames.has(localIt.name)) {
                    merged.push(localIt);
                } else {
                    // If itinerary exists on both, merge the sites
                    const serverIt = merged.find((it) => it.name === localIt.name);
                    if (serverIt && localIt.sites) {
                        const mergedSites = new Set([...serverIt.sites, ...localIt.sites]);
                        serverIt.sites = Array.from(mergedSites);
                    }
                }
            }
            return merged;
        },

        // Helper to trigger sync after any data change
        async triggerSync() {
            if (import.meta.server) return;

            // Debounce syncs to avoid too many requests
            if (this._syncTimeout) {
                clearTimeout(this._syncTimeout);
            }
            this._syncTimeout = setTimeout(() => {
                this.syncWithServer();
            }, 1000); // Wait 1 second after last change before syncing
        },

        // Call this when user logs in to merge and sync their data
        async onUserLogin() {
            if (import.meta.server) return;
            console.log('User logged in, merging local and server data...');
            // First load and merge data from server
            await this.loadFromServer();
            // The loadFromServer method already handles syncing if there's merged data
            // But we can force a sync to ensure everything is up to date
            if (this.syncEnabled) {
                await this.syncWithServer();
            }
        },

        // Call this when user logs out to clear sync status
        // Note: We intentionally keep hasLoggedInBefore set to true after logout
        // This ensures future logins on the same device won't merge local data again
        // Only reset hasLoggedInBefore if you want to treat the next login as first-time
        onUserLogout() {
            if (import.meta.server) return;
            console.log('User logged out, clearing sync status');
            this.lastSyncedAt = null;
            this.syncError = null;
            // Cancel any pending syncs
            if (this._syncTimeout) {
                clearTimeout(this._syncTimeout);
                this._syncTimeout = null;
            }
            if (this._retryTimeout) {
                clearTimeout(this._retryTimeout);
                this._retryTimeout = null;
            }
            this._retryAttempt = 0;
            // Optionally reset the login flag if you want next login to merge data
            // Uncomment the following lines if needed:
            // this.hasLoggedInBefore = false;
            // localStorage.setItem('hasLoggedInBefore' + CURRENT_YEAR, JSON.stringify(false));
        },
    },
});